import { t } from "@lingui/core/macro"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link, Navigate } from "@tanstack/react-router"
import { AlertCircle } from "lucide-react"
import { z } from "zod"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useProjectEditContext } from "@/features/project-editor/context/ProjectEditContext"
import { LocaleRequiredDialog } from "@/features/project-editor/optimization-workflow/components/LocaleRequiredDialog"
import {
  projectOptimizeQueryOptions,
  configurationJsonQueryOptions,
} from "@/features/project-editor/optimization-workflow/core/queryOptions"
import { OptimizeRoute as OptimizeRouteComponent } from "@/features/project-editor/optimization-workflow/optimizeRoute"
import { usePermissions } from "@/hooks/usePermissions"

const searchSchema = z.object({
  previewConfigVersion: z.string().optional(),
})

export const Route = createFileRoute("/(authenticated)/projects/edit/$projectId/optimize")({
  validateSearch: searchSchema,
  component: OptimizeRoutePage,
  // staleReloadMode: "blocking" forces TanStack Router to AWAIT the loader on
  // every revisit instead of rendering the cached match with previous
  // loaderData while the new loader runs in the background. Without this,
  // navigating back here right after saving entry/exit points on the Process
  // step would briefly render with the old loaderData (no entry point), then
  // re-render once the loader resolved (BLU-527 follow-up flicker fix).
  // fetchQuery (not ensureQueryData) ensures the query is actually refetched
  // when the cache entry is invalidated by forceSave.
  loader: {
    staleReloadMode: "blocking",
    handler: async ({ params, context }) => {
      const imageUrl = `/api/projects/${params.projectId}/image`

      const [projectData] = await Promise.all([
        context.queryClient.fetchQuery(projectOptimizeQueryOptions(params.projectId)),
        fetch(imageUrl, { method: "HEAD" }).catch(() => {}),
      ])

      const project = (
        projectData as {
          project: {
            configVersionId?: string | null
            configLocale?: string | null
            metadata: {
              entryPointPosition?: { x?: number; y?: number } | null
            }
          }
        }
      ).project
      await context.queryClient.fetchQuery(
        configurationJsonQueryOptions(
          project.configVersionId ?? null,
          project.configLocale ?? undefined,
        ),
      )

      const ep = project.metadata.entryPointPosition
      return { hasEntryPoint: ep?.x != null && ep?.y != null }
    },
  },
})

function OptimizeRoutePage() {
  const { projectId } = Route.useParams()
  const { previewConfigVersion } = Route.useSearch()
  const context = useProjectEditContext()

  // Access control: Block store_manager and external_viewer from project edit screens
  // Wait for permissions to load to avoid redirect flash on page refresh
  const { hasPermission, role, isLoading: permissionsLoading } = usePermissions()
  const canAccessEditScreen =
    hasPermission("project", "update").allowed || role === "store_designer"

  const { hasEntryPoint } = Route.useLoaderData()

  // The loader has already populated this query, so the suspense fence is
  // immediate — we read it here to gate on configLocale.
  const { data: projectData } = useSuspenseQuery(projectOptimizeQueryOptions(projectId))
  const project = (
    projectData as {
      project: { configLocale?: string | null; configVersionId?: string | null }
      storeLocale?: string | null
    }
  ).project
  const storeLocale = (projectData as { storeLocale?: string | null }).storeLocale ?? null

  // Don't redirect while permissions are still loading
  if (!permissionsLoading && !canAccessEditScreen) {
    return <Navigate to="/projects" />
  }

  // Block canvas if no entry point is set — user must define it on the Process page
  if (!hasEntryPoint) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <Card className="max-w-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              {t`Entry point required`}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground text-sm">
              {t`The store entry point (green dot) must be set before you can optimize assignments. Please go to the Process page and place the entry point on the floor plan.`}
            </p>
            <Link to="/projects/edit/$projectId/process" params={{ projectId }}>
              <Button>{t`Go to Process`}</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Use readOnly from parent context (includes edit mode + permission check)
  // Fallback to permission-only check if context not yet available
  const readOnly = context?.readOnly ?? !hasPermission("project", "update").allowed

  // Legacy projects (and any created via paths that didn't propagate a locale)
  // land here with configLocale=null. Force writers to pick one before they
  // can touch the canvas — read-only viewers can keep browsing.
  const needsLocale = !project.configLocale && !readOnly

  return (
    <>
      <OptimizeRouteComponent
        projectId={projectId}
        readOnly={readOnly}
        previewConfigVersion={previewConfigVersion}
      />
      {needsLocale && (
        <LocaleRequiredDialog
          projectId={projectId}
          configVersionId={project.configVersionId ?? null}
          storeLocale={storeLocale}
        />
      )}
    </>
  )
}
